import { useEffect, useMemo, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useQueryWithErrorHandler } from './useQueryWithErrorHandler';
import { refetchQueries } from './refetchQueries';
import {
  IApiParams,
  IChangeVehicleNotificationStateRequest,
  IError,
  IResponseData,
  IVehicle,
} from 'types';
import { useAuth, useError } from '../providers';
import { useAuthClient } from './useAuthClient';
import { firebaseAnalytics } from '../analytics';
import { errors } from '../config';

const vehicleQueryKey = 'getRoles';
const defaultSkip = 0;

export function useVehicleApi({
  queryParameters,
  organizationId,
  isInfiniteScrolling = true,
}: IApiParams) {
  const authClient = useAuthClient();
  const { openModal } = useError();
  const queryClient = useQueryClient();
  const { supplierId } = useAuth();
  const [vehicles, setVehicles] = useState<IVehicle[]>([]);

  const currentSupplierId = useMemo(() => {
    return organizationId || supplierId;
  }, [organizationId, supplierId]);

  const getVehiclesUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Vehicle/get-vehicles`;
  }, [currentSupplierId]);

  const changeVehicleNotificationsStateUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/Vehicle/change-enable-vehicles`;
  }, [currentSupplierId]);

  const {
    data: vehiclesResponse,
    refetch: fetchVehicles,
    isLoading: isFetchingVehicles,
    error: fetchVehiclesError,
  } = useQueryWithErrorHandler<IResponseData<IVehicle[]>, IError>({
    queryKey: [vehicleQueryKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getVehiclesUrl}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(errors.getVehicles, error, {
        getVehiclesUrl: getVehiclesUrl,
      });
      openModal(error);
    },

    placeholderData: keepPreviousData,
  });

  const {
    mutateAsync: changeVehicleNotificationState,
    isPending: isChangingVehicleNotificationState,
    isSuccess: isChangingVehicleNotificationStateSuccess,
    error: changeVehicleNotificationStateError,
  } = useMutation({
    mutationFn: (payload: IChangeVehicleNotificationStateRequest[]) =>
      authClient(changeVehicleNotificationsStateUrl, {
        method: 'PATCH',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [vehicleQueryKey],
      });
      return queryClient.getQueryData<boolean>([vehicleQueryKey]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<boolean>([vehicleQueryKey], context);
      }

      firebaseAnalytics.logErrorEvent(
        errors.changeVehicleNotificationState,
        error,
        {
          changeVehicleNotificationsStateUrl,
        },
      );
      openModal(error);
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [vehicleQueryKey],
      });
      await refetchQueries(queryClient, {
        queryKey: [vehicleQueryKey],
      });
    },
  });

  useEffect(() => {
    if (!isFetchingVehicles) {
      setVehicles((prevState: IVehicle[]) => {
        return queryParameters?.skip !== defaultSkip && isInfiniteScrolling
          ? vehiclesResponse?.data
            ? [...prevState, ...vehiclesResponse.data]
            : prevState
          : vehiclesResponse?.data || [];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehiclesResponse?.data]);

  return {
    vehicles,
    vehiclesResponse,
    fetchVehicles,
    isFetchingVehicles,
    fetchVehiclesError,
    changeVehicleNotificationState,
    isChangingVehicleNotificationState,
    isChangingVehicleNotificationStateSuccess,
    changeVehicleNotificationStateError,
  };
}
